import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Form,
    Container,
    Row,
    Col,
    InputGroup,
    Button,
    Spinner,
} from "react-bootstrap";
import { getAuthToken } from "../../utils/auth";
import PaginationBar from "../common/PaginationBar";
import { useNavigate } from "react-router-dom"; // ✅ import
import { ITEMS_PER_PAGE } from "../../constants/appConstants";

const UserManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();
    const navigate = useNavigate(); // ✅ init navigate

    const [agents, setAgents] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(0); // backend is 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const agentsPerPage = ITEMS_PER_PAGE;

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/admin/agents`);
            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", agentsPerPage);
            if (search.trim()) url.searchParams.append("query", search.trim());

            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch users:", res.statusText);
                return;
            }

            const result = await res.json();
            if (result.status) {
                setAgents(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
            } else {
                setAgents([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, agentsPerPage, currentPage, search]); // include both currentPage and search

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search]);

    const handleSearch = () => {
        setCurrentPage(0); // reset to first page when searching
    };

    const handleClear = () => {
        setSearch("");
        setCurrentPage(0);
    };

    // ✅ on row click
    const handleRowClick = (agentName) => {
        // navigate(`/admin/agents/${agentId}`);
        //navigate(`/admin/activation-codes`);
        navigate(`/admin/activation-codes?search=${encodeURIComponent(agentName)}`);
    };

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h3>Sale Agents </h3>
                </Col>

                <Col xs="5">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search query ..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        <Button variant="primary" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outline-secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Phone number</th>
                                <th>Address</th>
                                <th>Referral Code</th>
                                <th>Status</th>
                            </tr>
                        </thead>

                        <tbody>
                            {agents.length > 0 ? (
                                agents.map((agent) => (
                                    <tr
                                        key={agent.id}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleRowClick(agent.name)} // ✅ click to open details

                                    >
                                        <td>{agent.id}</td>
                                        <td>{agent.name}</td>
                                        <td>{agent.phone}</td>
                                        <td>
                                            {agent.address}
                                        </td>
                                        <td>
                                            {agent.referralCode}
                                        </td>

                                        <td>{agent.active ? "Active" : "Disabled"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        No sale agents found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default UserManagement;
